import React, { useState } from "react";
import { toast } from "sonner";
import { Printer, Globe, Check, X, Utensils } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

async function download(path, name) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = name; a.click();
  } catch (e) {
    let msg = "Download failed";
    try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
    toast.error(msg);
  }
}

export function SeatingToolbar({ d, slug, reload }) {
  async function publish(v) {
    try { await api.put("/weddings/mine/seating-public", { public: v }); toast.success(v ? "Seating chart is live on your wedding site" : "Seating chart hidden"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="seating-toolbar">
      <button onClick={() => download("/tables/print.pdf", "seating-chart.pdf")} className="btn-outline-gold !py-2 text-sm flex items-center gap-2" data-testid="seating-print-chart"><Printer className="w-4 h-4" /> Print chart</button>
      <button onClick={() => download("/tables/place-cards.pdf", "place-cards.pdf")} className="btn-outline-gold !py-2 text-sm flex items-center gap-2" data-testid="seating-print-cards"><Printer className="w-4 h-4" /> Place cards</button>
      <button onClick={() => download(`/tables/qr-signs.pdf?origin=${encodeURIComponent(window.location.origin)}`, "table-qr-signs.pdf")} className="btn-outline-gold !py-2 text-sm flex items-center gap-2" data-testid="seating-print-qr"><Printer className="w-4 h-4" /> Table QR signs</button>
      <label className="flex items-center gap-2 text-sm ml-auto"><input type="checkbox" checked={!!d?.seating_public} onChange={(e) => publish(e.target.checked)} data-testid="seating-public-toggle" /> <Globe className="w-4 h-4 text-[var(--gold)]" /> Show on wedding site</label>
      {d?.seating_public && slug && <a href={`/site/${slug}/seating`} target="_blank" rel="noreferrer" className="text-xs text-[var(--gold)] underline" data-testid="seating-public-link">View</a>}
    </div>
  );
}

export function MenuEditor({ options, reload }) {
  const [text, setText] = useState(options.join(", "));
  async function save() {
    try { await api.put("/weddings/mine/meal-options", { options: text.split(",") }); toast.success("Menu saved. Guests can pick when they RSVP."); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-4 mb-4 flex flex-col sm:flex-row gap-2 sm:items-center" data-testid="menu-editor">
      <p className="text-sm font-medium flex items-center gap-2 shrink-0"><Utensils className="w-4 h-4 text-[var(--gold)]" /> Menu</p>
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Filet mignon, Salmon, Wild mushroom risotto, Kids meal" className="input-luxe text-sm flex-1" data-testid="menu-input" />
      <button onClick={save} className="btn-gold !py-2 text-sm" data-testid="menu-save">Save menu</button>
    </div>
  );
}

export function GuestDetailRow({ g, options, tableName, reload }) {
  async function save(p) {
    try { await api.patch(`/guests/${g.guest_id}/seating`, p); reload(); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="grid grid-cols-[1fr_auto] gap-1 py-2 border-b border-[var(--line)] text-xs" data-testid={`guest-detail-${g.guest_id}`}>
      <p className="font-medium text-sm truncate">{g.name} <span className="text-[var(--muted)] font-normal">{tableName ? `· ${tableName}` : "· not seated"}</span></p>
      <select value={g.meal_preference || ""} onChange={(e) => save({ meal_preference: e.target.value })} className="border border-[var(--line)] rounded px-1 py-0.5 bg-white max-w-[120px]" data-testid={`guest-meal-${g.guest_id}`}>
        <option value="">Meal…</option>
        {[...new Set([...options, g.meal_preference].filter(Boolean))].map((o) => <option key={o}>{o}</option>)}
      </select>
      <input defaultValue={g.dietary || ""} onBlur={(e) => e.target.value !== (g.dietary || "") && save({ dietary: e.target.value })} placeholder="Dietary / allergies" className="border border-[var(--line)] rounded px-2 py-0.5" data-testid={`guest-dietary-${g.guest_id}`} />
      <input defaultValue={g.seat_group || ""} onBlur={(e) => e.target.value !== (g.seat_group || "") && save({ seat_group: e.target.value })} placeholder="Keep together…" className="border border-[var(--line)] rounded px-2 py-0.5 w-[120px]" data-testid={`guest-group-${g.guest_id}`} />
    </div>
  );
}

export function SeatRequests({ rows, reload }) {
  async function decide(id, status) {
    try { const { data } = await api.patch(`/seating/requests/${id}`, { status }); toast.success(status === "approved" ? (data.moved ? "Approved and moved" : "Approved. Move them by hand if needed.") : "Declined"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  const pending = rows.filter((r) => r.status === "pending");
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-4 mt-6" data-testid="seat-requests">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Guest seating requests ({pending.length} waiting)</p>
      {rows.slice(0, 30).map((r) => (
        <div key={r.request_id} className="flex items-start gap-3 py-2 border-b border-[var(--line)] text-sm" data-testid={`seat-request-${r.request_id}`}>
          <div className="flex-1">
            <p><span className="font-medium">{r.name}</span> {r.kind === "move" ? <>asks to move to <b>{r.table_name || "another table"}</b></> : <>wants to sit with <b>{r.sit_with}</b>{r.table_name ? ` at ${r.table_name}` : ""}</>}</p>
            {r.note && <p className="text-xs text-[var(--muted)]">“{r.note}”</p>}
          </div>
          {r.status === "pending" ? (
            <div className="flex gap-1">
              <button onClick={() => decide(r.request_id, "approved")} className="p-1.5 rounded-full bg-green-50 text-green-700" aria-label="Approve" data-testid={`seat-approve-${r.request_id}`}><Check className="w-4 h-4" /></button>
              <button onClick={() => decide(r.request_id, "declined")} className="p-1.5 rounded-full bg-red-50 text-red-600" aria-label="Decline" data-testid={`seat-decline-${r.request_id}`}><X className="w-4 h-4" /></button>
            </div>
          ) : <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{r.status}</span>}
        </div>
      ))}
    </div>
  );
}
